/**
 * City Lookup Service (Open-Meteo Geocoding)
 *
 * Resolves a city name to its country using the Open-Meteo geocoding API.
 * Documentation: https://open-meteo.com/en/docs/geocoding-api
 *
 * Features:
 * - Free, no API key required, CORS enabled
 * - Worldwide city coverage (GeoNames data)
 * - Caching to reduce API calls
 * - Hardcoded fallback for common cities when the API is unreachable
 */

const GEOCODING_API_BASE = 'https://geocoding-api.open-meteo.com/v1';

interface GeocodingResult {
  name: string;
  country?: string;
  country_code?: string;
}

interface GeocodingResponse {
  results?: GeocodingResult[];
}

class CityLookupService {
  private cache: Map<string, { data: string | null; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds (city-country mappings don't change often)

  /**
   * Get country name for a given city
   * @param cityName - Name of the city to lookup
   * @returns Country name or null if not found
   */
  async getCityCountry(cityName: string): Promise<string | null> {
    const normalizedCityName = cityName.toLowerCase().trim();
    const cacheKey = normalizedCityName;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log(`Using cached country for city: ${cityName}`);
      return cached.data;
    }

    try {
      const url = `${GEOCODING_API_BASE}/search?name=${encodeURIComponent(cityName)}&count=1&language=en&format=json`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Open-Meteo geocoding error: ${response.status} ${response.statusText}`);
      }

      const data: GeocodingResponse = await response.json();
      const country = data.results?.[0]?.country || null;

      // Cache the result (including null, to avoid repeated lookups for unknown cities)
      this.cache.set(cacheKey, { data: country, timestamp: Date.now() });

      return country;
    } catch (error) {
      console.error(`Error fetching city info for ${cityName}:`, error);

      // Fall back to the hardcoded map on network/API errors (result not cached,
      // so the API is retried once it recovers)
      return this.getFallbackCountry(normalizedCityName);
    }
  }

  /**
   * Fallback country mapping for common cities
   * Used when API is unavailable or rate limited
   */
  private getFallbackCountry(cityName: string): string | null {
    const cityToCountry: Record<string, string> = {
      'zurich': 'Switzerland',
      'frankfurt': 'Germany',
      'paris': 'France',
      'london': 'United Kingdom',
      'berlin': 'Germany',
      'munich': 'Germany',
      'amsterdam': 'Netherlands',
      'brussels': 'Belgium',
      'vienna': 'Austria',
      'rome': 'Italy',
      'milan': 'Italy',
      'madrid': 'Spain',
      'barcelona': 'Spain',
      'lisbon': 'Portugal',
      'dublin': 'Ireland',
      'copenhagen': 'Denmark',
      'stockholm': 'Sweden',
      'oslo': 'Norway',
      'helsinki': 'Finland',
      'athens': 'Greece',
      'tel aviv': 'Israel',
      'jerusalem': 'Israel',
      'haifa': 'Israel',
      'new york': 'United States',
      'los angeles': 'United States',
      'chicago': 'United States',
      'houston': 'United States',
      'miami': 'United States',
      'san francisco': 'United States',
      'toronto': 'Canada',
      'vancouver': 'Canada',
      'montreal': 'Canada',
      'sydney': 'Australia',
      'melbourne': 'Australia',
      'tokyo': 'Japan',
      'osaka': 'Japan',
      'beijing': 'China',
      'shanghai': 'China',
      'hong kong': 'Hong Kong',
      'singapore': 'Singapore',
      'dubai': 'United Arab Emirates',
      'mumbai': 'India',
      'delhi': 'India',
      'bangalore': 'India',
      'seoul': 'South Korea',
      'bangkok': 'Thailand',
      'istanbul': 'Turkey',
      'moscow': 'Russia',
      'st petersburg': 'Russia',
      'warsaw': 'Poland',
      'prague': 'Czech Republic',
      'budapest': 'Hungary',
      'bucharest': 'Romania',
      'cairo': 'Egypt',
      'johannesburg': 'South Africa',
      'cape town': 'South Africa',
      'buenos aires': 'Argentina',
      'sao paulo': 'Brazil',
      'rio de janeiro': 'Brazil',
      'mexico city': 'Mexico',
      'bogota': 'Colombia',
      'lima': 'Peru',
      'santiago': 'Chile',
    };

    return cityToCountry[cityName] || null;
  }

  /**
   * Extract destination city from flight description
   * @param flightDescription - Flight description like "Tel Aviv to Zurich"
   * @returns City name or null if not found
   */
  extractDestinationCity(flightDescription: string): string | null {
    // Try to extract destination from "City1 to City2" pattern
    const toPattern = /to\s+([A-Za-z\s]+?)(?:\s*-|$)/i;
    const match = flightDescription.match(toPattern);

    if (match && match[1]) {
      return match[1].trim();
    }

    return null;
  }

  /**
   * Clear the cache (useful for testing or when data needs to be refreshed)
   */
  clearCache(): void {
    this.cache.clear();
  }
}

export const cityLookupService = new CityLookupService();
